/**
 * Lightweight modal overlay. Renders a translucent backdrop with a
 * centred panel. Clicks on the backdrop dismiss; Escape closes too.
 *
 * Intentionally minimal: no portal (mounts inline), no scroll lock (we
 * never have body scroll yet), no focus trap (one input per modal so
 * the browser handles it well enough). All of those land later if/when
 * we hit a real need.
 */
import { useEffect } from "react";
import type { MouseEvent, ReactNode } from "react";
import "./Modal.css";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  titleId?: string;
  children: ReactNode;
}

export function Modal({ open, onClose, titleId, children }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div className="modal-backdrop" onMouseDown={handleBackdropClick}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {children}
      </div>
    </div>
  );
}
